/* import __COLOCATED_TEMPLATE__ from './security-tabbed.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import { type TabItem } from '../../common/standard-base';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { AUTH_METHODS } from 'embercom/components/new-settings/workspace/security/authentication-methods';
import { shouldShowLinkToSamlArticle } from 'embercom/lib/saml';
import { getSamlVerificationErrorMessage } from 'embercom/lib/saml';
import { post } from 'embercom/lib/ajax';
import { SAML_PROVISIONING_METHODS } from 'embercom/controllers/apps/app/settings/workspace/security';
import { underscore } from '@ember/string';
import { buildDefaultScimEndpointUrl } from 'embercom/models/scim-setting';

interface SecurityTabbedArgs {
  selectedTab?: string;
  settings: any;
  attachmentSettings: any;
  initialProvisioningMethod: string;
  selectedProvisioningMethod: string;
  changeTab: (tab: string, section?: string) => void;
}

interface Signature {
  Args: SecurityTabbedArgs;
}

export default class SecurityTabbed extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;
  @service declare intercomEventService: $TSFixMe;
  @service declare securityAppSettingsService: $TSFixMe;
  @service declare identityVerificationRiskStateService: $TSFixMe;
  @service declare modelDataCacheService: $TSFixMe;

  @tracked initialProvisioningMethod = this.args.initialProvisioningMethod;
  @tracked selectedProvisioningMethod = this.args.selectedProvisioningMethod;
  @tracked showEnforceSamlModal = false;
  @tracked showSamlVerificationModal = false;
  @tracked showEnforceSsoModal = false;
  @tracked showEnforce2faModal = false;
  @tracked showAllErrors = false;
  @tracked isSaveAttachmentSettingsRunning = false;
  @tracked settings = this.args.settings;

  constructor(owner: unknown, args: SecurityTabbedArgs) {
    super(owner, args);
    window.addEventListener('message', this.samlVerificationCompletedHandler);
  }

  willDestroy(): void {
    window.removeEventListener('message', this.samlVerificationCompletedHandler);
    super.willDestroy();
  }

  get selectedTab() {
    return this.args.selectedTab || 'workspace-security';
  }

  get app() {
    return this.appService.app;
  }

  get tabs(): Array<TabItem> {
    let tabs = [
      {
        value: 'workspace-security',
        label: 'new-settings.workspace.security.tabs.workspace',
      },
      {
        value: 'data-security',
        label: 'new-settings.workspace.security.tabs.data',
      },
      {
        value: 'messenger-security',
        label: 'new-settings.workspace.security.tabs.messenger',
      },
      {
        value: 'conversation-attachments',
        label: 'new-settings.workspace.security.tabs.attachments',
      },
      {
        value: 'links',
        label: 'new-settings.workspace.security.tabs.links',
      },
    ];

    if (this.app.canUseWorkflowConnectorIdentityPolicy) {
      tabs.push({
        value: 'customer-authentication',
        label: 'new-settings.workspace.security.tabs.customer-authentication',
      });
    }

    // Add health-check tab if feature flag is enabled and not standalone app
    if (!this.app.isStandaloneApp && this.app.canUseSecurityHealthCheck) {
      tabs.push({
        value: 'health-check',
        label: 'new-settings.workspace.security.tabs.health-check',
      });
    }

    if (this.app.isStandaloneApp) {
      tabs = this.filterTabsForStandalone(tabs);
    }

    return tabs;
  }

  get isSaveDataSecurityDisabled() {
    return this.isInvalidSecurityEmail || this.isDataSecuritySettingsSaving || !this.isDirty;
  }

  get isDataSecuritySettingsSaving(): boolean {
    return taskFor(this.saveDataSecurityChanges).isRunning;
  }

  get isInvalidSecurityEmail() {
    if (
      this.args.settings.securityContactEmail === '' ||
      this.args.settings.securityContactEmail === null
    ) {
      return false;
    }
    return !this.isValidEmail(this.args.settings.securityContactEmail);
  }

  get canEnforceSaml() {
    return this.args.settings.canEnforceSaml;
  }

  get attachmentSettings() {
    return (
      this.args.attachmentSettings || this.store.peekRecord('attachment-settings', this.app.id)
    );
  }

  get shouldEnableUnauthenticatedUserMerge() {
    return this.webIdentityVerified || this.iosIdentityVerified || this.androidIdentityVerified;
  }

  get webIdentityVerified() {
    return this.app.identityVerified;
  }

  get iosSdkApp() {
    return this.app.iosSdkApps.firstObject;
  }

  get iosIdentityVerified() {
    return this.iosSdkApp.identityVerified;
  }

  get androidSdkApp() {
    return this.app.androidSdkApps.firstObject;
  }

  get androidIdentityVerified() {
    return this.androidSdkApp.identityVerified;
  }

  get samlAccount() {
    return this.args.settings.samlAccount;
  }

  get canSaveAttachmentSettings() {
    let modelCanBeSaved =
      !this.attachmentSettings.allowedAttachmentFiletypesEnabled ||
      this.attachmentSettings.allowedAttachmentFiletypesAgreedToRisk;
    let taskIdle = !taskFor(this.saveAttachmentSettings).isRunning;

    return (
      taskIdle &&
      this.attachmentSettings.get('hasDirtyAttributes') &&
      modelCanBeSaved &&
      this.attachmentSettings.isFiletypeListValid
    );
  }

  get isSamlVerifyInProgress(): boolean {
    return taskFor(this.startSamlVerification).isRunning;
  }

  get hasEnabled2fa() {
    return this.app.currentAdmin.has_enabled_2fa;
  }

  get canEnforce2fa() {
    return this.args.settings.can_enforce_2fa && this.hasEnabled2fa;
  }

  get enablingGoogleAuthOnly() {
    return (
      this.authMethods.length === 1 &&
      this.authMethods[0] === AUTH_METHODS.google &&
      this.authMethodsChanged
    );
  }

  get enabling2faOnly() {
    return (
      this.authMethods.length === 1 &&
      this.authMethods[0] === AUTH_METHODS.twoFa &&
      this.authMethodsChanged
    );
  }

  get authMethods() {
    return this.args.settings.authorizedSignInMethods;
  }

  get authMethodsChanged() {
    return 'authorizedSignInMethods' in this.args.settings.changedAttributes();
  }

  get isScimSettingsDirty() {
    return this.scimEnabled && this.args.settings.scimSettings?.hasDirtyAttributes;
  }

  get isSamlSettingsDirty() {
    return this.samlAllowed && this.samlAccount?.hasDirtyAttributes;
  }

  get hasChangedProvisioningMethod() {
    return this.initialProvisioningMethod !== this.selectedProvisioningMethod;
  }

  get scimEnabled() {
    return this.selectedProvisioningMethod === SAML_PROVISIONING_METHODS.scim;
  }

  get isDirty() {
    return (
      this.args.settings.hasDirtyAttributes ||
      this.isSamlSettingsDirty ||
      this.isScimSettingsDirty ||
      this.hasChangedProvisioningMethod
    );
  }

  get samlAllowed() {
    return this.authMethods.includes(AUTH_METHODS.saml);
  }

  get hasChangedSamlSettings() {
    return this.enablingSaml || this.isSamlSettingsDirty;
  }

  get enablingSaml() {
    let changes = this.args.settings.changedAttributes();
    if (!('authorizedSignInMethods' in changes)) {
      return false;
    }

    let [oldMethods, newMethods] = changes.authorizedSignInMethods;
    let samlWasAdded =
      !oldMethods.includes(AUTH_METHODS.saml) && newMethods.includes(AUTH_METHODS.saml);

    return samlWasAdded;
  }

  get samlAuth() {
    return [AUTH_METHODS.saml];
  }

  redirectTo2faSetup() {
    window.location.href = '/admins/generate_otp';
  }

  redirectToSignOut() {
    window.location.href = '/admins/sign_out';
  }

  @action
  toggleUserAttachmentSettingsEnabled() {
    let newValue = !this.attachmentSettings.userConversationAttachmentsEnabled;
    this.attachmentSettings.set('userConversationAttachmentsEnabled', newValue);
    this.attachmentSettings.set('userConversationCameraEnabled', newValue);
    this.attachmentSettings.set('userConversationMediaEnabled', newValue);
    this.attachmentSettings.set('userConversationGifsEnabled', newValue);
    this.attachmentSettings.set('userConversationFilesEnabled', newValue);

    if (this.appService.app.hasConsentedToExternalAi) {
      this.attachmentSettings.set('userConversationVoiceNotesEnabled', newValue);
    }

    if (!newValue) {
      this.attachmentSettings.set('allowedAttachmentFiletypesEnabled', false);
      this.attachmentSettings.set('allowedAttachmentFiletypesAgreedToRisk', false);
    }
  }

  @action
  changeTab(tabValue: string) {
    this.args.changeTab(tabValue);
  }

  @action
  openArticle(id: number, name: string) {
    window.Intercom('showArticle', id);
    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      object: `learn_about_${name}`,
      place: 'security_settings',
    });
  }

  @action
  save() {
    if (this.enablingGoogleAuthOnly) {
      this.showEnforceSsoModal = true;
    } else if (this.enabling2faOnly && !this.canEnforce2fa) {
      this.showEnforce2faModal = true;
    } else {
      taskFor(this.saveDataSecurityChanges).perform();
    }
  }

  @action
  samlVerificationCompletedHandler(event: MessageEvent) {
    let expectedEventType = 'saml-verification-completed';
    let expectedOrigin = window.location.origin;
    let eventData;
    try {
      eventData = JSON.parse(event.data);
    } catch {
      eventData = null;
    }

    if (!eventData || eventData.type !== expectedEventType || event.origin !== expectedOrigin) {
      return;
    }

    if (eventData.success === true) {
      this.intercomEventService?.trackAnalyticsEvent({
        action: 'verified',
        object: 'verify_saml',
      });
      taskFor(this.saveDataSecurityChanges).perform(true);
    } else {
      let { error_code: errorCode, message } = eventData;
      let errorMessage = getSamlVerificationErrorMessage(errorCode, message);
      let showLearnMoreButton = shouldShowLinkToSamlArticle(errorCode);

      if (showLearnMoreButton) {
        this.notificationsService.notifyErrorWithButton(
          errorMessage,
          {
            label: this.intl.t('new-settings.workspace.security.learn-more'),
            action: () =>
              safeWindowOpen(
                'https://www.intercom.com/help/en/articles/3974587-Integrate-with-an-identity-provider-and-log-in-with-SAML-SSO',
              ),
          },
          0,
        );
      } else {
        this.notificationsService.notifyError(errorMessage, 0);
      }
    }
  }

  @action
  updateProvisioningMethod(selectedProvisioningMethod: string) {
    this.selectedProvisioningMethod = selectedProvisioningMethod;

    if (this.scimEnabled && !this.args.settings.scimSettings) {
      this.args.settings.set(
        'scimSettings',
        this.store.createRecord('scim-setting', {
          immuneAdminIds: [],
          scimEndpointUrl: buildDefaultScimEndpointUrl(this.appService.app.id),
        }),
      );
    }
  }

  @dropTask
  *startSamlVerification(): any {
    this.notificationsService.clear();
    try {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'verify_saml',
        jit_enabled: this.samlAccount.jitEnabled,
      });
      yield this.samlAccount.save();

      let response = yield post(`${this.args.settings.samlBaseUrl}/verify`);
      if (response && response.redirectUrl) {
        safeWindowOpen(response.redirectUrl);
      } else {
        throw new Error('No redirect URL received from SAML verification endpoint');
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('apps.app.settings.security.error-starting'),
      );
    }
  }

  @dropTask
  *saveDataSecurityChanges(runJustSave = false): any {
    if (this.hasChangedSamlSettings && !runJustSave) {
      this.args.settings.set('samlAccount', this.args.settings.samlAccount);

      if (!this.scimEnabled) {
        this.args.settings.set('scimSettings', null);
      }

      if (this.authMethods === this.samlAuth && !this.canEnforceSaml) {
        this.showEnforceSamlModal = true;
      } else if (this.args.settings.samlAccount.isValid) {
        this.showSamlVerificationModal = true;
      } else {
        this.showAllErrors = true;
        this.notificationsService.notifyError(
          this.intl.t('apps.app.settings.security.security-settings-not-saved'),
        );
      }
    } else {
      if (!runJustSave) {
        this.args.settings.set('samlAccount', null);
        if (!(this.samlAllowed && this.scimEnabled)) {
          this.args.settings.set('scimSettings', null);
        }
      }
      try {
        let changedFields = this.args.settings.changedAttributes();
        yield this.args.settings.save();

        if (this.args.settings.scimSettings?.hasDirtyAttributes) {
          this.args.settings.scimSettings.rollbackAttributes();
        }
        if (this.args.settings.samlAccount?.hasDirtyAttributes) {
          this.args.settings.samlAccount.rollbackAttributes();
        }

        yield this.appService.app.reload();
        yield this.appService.app.updateLocalCache(this.modelDataCacheService);
        this.securityAppSettingsService.set(
          'settings.notificationEmailsWithContentEnabled',
          this.args.settings.notificationEmailsWithContentEnabled,
        );
        this.showSamlVerificationModal = false;
        this.showEnforce2faModal = false;
        this.showEnforceSsoModal = false;
        this.initialProvisioningMethod = this.selectedProvisioningMethod;
        this.notificationsService.notifyConfirmation(
          this.intl.t('apps.app.settings.security.security-settings-updated'),
        );
        this.identityVerificationRiskStateService.start(this.appService.app);
        this._trackAnalyticsEvent(changedFields);
      } catch (err) {
        let notificationText;
        if (err.jqXHR?.responseJSON?.errors[0]) {
          notificationText = err.jqXHR.responseJSON.errors[0];
        } else {
          notificationText = this.intl.t('apps.app.settings.security.settings-not-updated');
        }
        this.notificationsService.notifyError(notificationText);
      }
    }
  }

  @dropTask
  *saveAttachmentSettings(): any {
    try {
      this.isSaveAttachmentSettingsRunning = true;
      yield this.attachmentSettings.save();

      this.notificationsService.notifyConfirmation(
        this.intl.t('apps.app.settings.attachment-settings.save-successful'),
      );
      this.isSaveAttachmentSettingsRunning = false;
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('apps.app.settings.attachment-settings.trusted-domains-error-message'),
      });
    }
  }

  private isValidEmail(email: string): boolean {
    let emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private _trackAnalyticsEvent(changes: Record<string, [unknown, unknown]>) {
    try {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'saved',
        object: 'security_app_settings',
        models: [this.args.settings],
        changed_fields: Object.keys(changes).map(underscore),
        ...Object.fromEntries(
          Object.entries(changes).flatMap(([field, [oldVal, newVal]]) => [
            [`previous_${underscore(field)}`, oldVal],
            [`new_${underscore(field)}`, newVal],
          ]),
        ),
      });
    } catch (error) {
      console.error('Failed to track analytics event:', error);
    }
  }

  private filterTabsForStandalone(tabs: TabItem[]): TabItem[] {
    let tabsToReject = this.app.canSeeDataSecurityInStandalone
      ? ['links']
      : ['data-security', 'links'];

    return tabs.reject((tab) => tabsToReject.includes(tab.value));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Security::SecurityTabbed': typeof SecurityTabbed;
    'new-settings/workspace/security/security-tabbed': typeof SecurityTabbed;
  }
}
